import React from "react";
import { motion } from "framer-motion";
import {
  Gauge, Factory, ShieldCheck, ClipboardCheck, Ship, Container,
  Wallet, Activity, LayoutDashboard, Boxes, FileText, Settings,
} from "lucide-react";
import { Reveal, SectionHeading, easeOut } from "./primitives";

const Widget = ({ children, className = "", testid }) => (
  <div data-testid={testid} className={`rounded-xl border border-slate-200/80 bg-white p-4 ${className}`}>
    {children}
  </div>
);

const WTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
    <Icon className="h-3.5 w-3.5" />
    {children}
  </div>
);

export default function DashboardSection() {
  return (
    <section id="workspace" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="Bulk Procurement Dashboard"
            title="Your entire bulk order, on one screen."
            subtitle="A dedicated Workspace screen for a single bulk order — connected, minimal and enterprise-grade."
          />
        </Reveal>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: easeOut }}
          className="mt-14 overflow-hidden rounded-2xl border border-slate-200/80 bg-slate-50/50 shadow-[0_40px_80px_-32px_rgba(10,17,40,0.22)]"
        >
          <div className="grid grid-cols-1 lg:grid-cols-[210px_1fr]">
            <div className="hidden flex-col border-r border-slate-200/80 bg-white p-4 lg:flex">
              <div className="flex items-center gap-2 px-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#0052FF] text-white">
                  <Boxes className="h-4 w-4" />
                </span>
                <span className="font-display text-sm font-bold text-[#0A1128]">Workspace</span>
              </div>
              <nav className="mt-6 space-y-1">
                {[
                  { icon: LayoutDashboard, label: "Overview", active: true },
                  { icon: Factory, label: "Production" },
                  { icon: ClipboardCheck, label: "Inspection" },
                  { icon: Ship, label: "Freight" },
                  { icon: FileText, label: "Documents" },
                  { icon: Settings, label: "Settings" },
                ].map((n) => (
                  <div
                    key={n.label}
                    className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium ${
                      n.active ? "bg-[#EBF2FF] text-[#0052FF]" : "text-[#64748B]"
                    }`}
                  >
                    <n.icon className="h-4 w-4" />
                    {n.label}
                  </div>
                ))}
              </nav>
              <div className="mt-auto rounded-xl bg-slate-50 p-3">
                <p className="text-[11px] font-medium text-[#94A3B8]">Active Order</p>
                <p className="font-display text-sm font-bold text-[#0A1128]">BC-4821</p>
                <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[10px] font-semibold text-[#059669]">
                  Production Confirmed
                </span>
              </div>
            </div>

            <div className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono-ui text-[11px] text-[#94A3B8]">Bulk Order · BC-4821</p>
                  <h3 className="font-display text-lg font-bold text-[#0A1128]">
                    Wheat Flour · 500 MT · Kenya
                  </h3>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ECFDF5] px-3 py-1 text-[12px] font-semibold text-[#059669] ring-1 ring-inset ring-[#10B981]/20">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> On Schedule
                </span>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Widget className="col-span-2 lg:col-span-2" testid="widget-production">
                  <WTitle icon={Gauge}>Production Progress</WTitle>
                  <div className="mt-3 flex items-end justify-between">
                    <span className="font-display text-2xl font-bold text-[#0A1128]">68%</span>
                    <span className="text-[11px] font-medium text-[#059669]">+12% this week</span>
                  </div>
                  <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#0052FF] to-[#3B82F6]" style={{ width: "68%" }} />
                  </div>
                  <div className="mt-4 flex items-end gap-1.5">
                    {[40, 55, 48, 62, 70, 65, 78, 72].map((h, i) => (
                      <div key={i} className="flex-1 rounded-t bg-[#EBF2FF]" style={{ height: h }}>
                        <div className="h-1 w-full rounded-t bg-[#0052FF]/60" />
                      </div>
                    ))}
                  </div>
                </Widget>

                <Widget testid="widget-capacity">
                  <WTitle icon={Factory}>Factory Capacity</WTitle>
                  <p className="font-display mt-3 text-xl font-bold text-[#0A1128]">1,200</p>
                  <p className="text-[11px] text-[#94A3B8]">MT / month · 42% allocated</p>
                </Widget>

                <Widget testid="widget-quality">
                  <WTitle icon={ShieldCheck}>Quality Status</WTitle>
                  <p className="font-display mt-3 text-xl font-bold text-[#059669]">Passed</p>
                  <p className="text-[11px] text-[#94A3B8]">Batch QC · Grade A</p>
                </Widget>

                <Widget testid="widget-inspection">
                  <WTitle icon={ClipboardCheck}>Inspection</WTitle>
                  <p className="font-display mt-3 text-lg font-bold text-[#0A1128]">Scheduled</p>
                  <p className="text-[11px] text-[#94A3B8]">Pre-shipment · Apr 22</p>
                </Widget>

                <Widget testid="widget-freight">
                  <WTitle icon={Ship}>Freight Booking</WTitle>
                  <p className="font-display mt-3 text-lg font-bold text-[#0052FF]">FreightIQ™</p>
                  <p className="text-[11px] text-[#94A3B8]">Awaiting confirmation</p>
                </Widget>

                <Widget testid="widget-shipment">
                  <WTitle icon={Activity}>Shipment Status</WTitle>
                  <p className="font-display mt-3 text-lg font-bold text-[#0A1128]">Pending</p>
                  <p className="text-[11px] text-[#94A3B8]">ETD Apr 26 · Mersin</p>
                </Widget>

                <Widget testid="widget-container">
                  <WTitle icon={Container}>Container Allocation</WTitle>
                  <p className="font-display mt-3 text-xl font-bold text-[#0A1128]">20 × 20ft</p>
                  <p className="text-[11px] text-[#94A3B8]">FCL · 25 kg bags</p>
                </Widget>

                <Widget testid="widget-value">
                  <WTitle icon={Wallet}>Order Value</WTitle>
                  <p className="font-display mt-3 text-xl font-bold text-[#0A1128]">$186,000</p>
                  <p className="text-[11px] text-[#059669]">Factory direct · no markup</p>
                </Widget>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
